import { z } from 'zod';
import { normalizeOptionalBuildTemplateId } from '@/IsekaiRPG/modules/character/characterSemantic';

// ============================================
// ChatInteraction 动作协议
// ============================================

export const ChatInteractionActionTypeSchema = z.enum([
  'party_add_member',
  'party_add_known_member',
  'invoke_generation_task',
  'semantic_quest_create',
  'semantic_quest_generate_site',
  'semantic_quest_turn_in',
]);
export type ChatInteractionActionType = z.infer<typeof ChatInteractionActionTypeSchema>;

export const ConversationExposedActionTypeSchema = z.enum([
  'party_add_member',
  'party_add_known_member',
  'semantic_quest_create',
  'semantic_quest_turn_in',
]);
export type ConversationExposedActionType = z.infer<typeof ConversationExposedActionTypeSchema>;

export const PartyAddMemberParamsSchema = z.object({
  description: z.string().min(1),
  buildTemplateId: z.preprocess(
    value => normalizeOptionalBuildTemplateId(typeof value === 'string' ? value : undefined),
    z.string().min(1).optional(),
  ),
});
export type PartyAddMemberParams = z.infer<typeof PartyAddMemberParamsSchema>;

const PartyAddKnownMemberParamsInputSchema = z
  .object({
    characterId: z.string().min(1).optional(),
    query: z.string().min(1).optional(),
    // 兼容旧存档/旧协议里的 name 字段，统一折叠进 query。
    name: z.string().min(1).optional(),
  })
  .transform(value => ({
    characterId: value.characterId?.trim() || undefined,
    query: value.query?.trim() || value.name?.trim() || undefined,
  }));

export const PartyAddKnownMemberParamsSchema = PartyAddKnownMemberParamsInputSchema.refine(
  value => Boolean(value.characterId || value.query),
  {
    message: 'characterId/query 至少提供一个',
  },
);
export type PartyAddKnownMemberParams = z.infer<typeof PartyAddKnownMemberParamsSchema>;

export const GenerationTaskSchema = z.enum(['character_generate', 'item_generate']);
export type GenerationTask = z.infer<typeof GenerationTaskSchema>;

export const GenerationTaskContextModeSchema = z.enum(['full_context', 'compact_context']);
export type GenerationTaskContextMode = z.infer<typeof GenerationTaskContextModeSchema>;

export const InvokeGenerationTaskParamsSchema = z.object({
  task: GenerationTaskSchema,
  request: z.string().min(1),
  contextMode: GenerationTaskContextModeSchema.default('full_context'),
});
export type InvokeGenerationTaskParams = z.infer<typeof InvokeGenerationTaskParamsSchema>;

export const SemanticQuestCreateParamsSchema = z.object({
  request: z.string().min(1),
});
export type SemanticQuestCreateParams = z.infer<typeof SemanticQuestCreateParamsSchema>;

export const SemanticQuestReferenceParamsSchema = z.object({
  questId: z.string().min(1),
  questTitle: z.string().min(1).optional(),
});
export type SemanticQuestReferenceParams = z.infer<typeof SemanticQuestReferenceParamsSchema>;

export const SemanticQuestGenerateSiteParamsSchema = SemanticQuestReferenceParamsSchema;
export type SemanticQuestGenerateSiteParams = z.infer<typeof SemanticQuestGenerateSiteParamsSchema>;

export const SemanticQuestTurnInParamsSchema = SemanticQuestReferenceParamsSchema;
export type SemanticQuestTurnInParams = z.infer<typeof SemanticQuestTurnInParamsSchema>;

const PartyAddMemberActionSchema = z.object({
  action: z.literal('party_add_member'),
  params: PartyAddMemberParamsSchema,
});

const PartyAddKnownMemberActionSchema = z.object({
  action: z.literal('party_add_known_member'),
  params: PartyAddKnownMemberParamsSchema,
});

const InvokeGenerationTaskActionSchema = z.object({
  action: z.literal('invoke_generation_task'),
  params: InvokeGenerationTaskParamsSchema,
});

const SemanticQuestCreateActionSchema = z.object({
  action: z.literal('semantic_quest_create'),
  params: SemanticQuestCreateParamsSchema,
});

const SemanticQuestGenerateSiteActionSchema = z.object({
  action: z.literal('semantic_quest_generate_site'),
  params: SemanticQuestGenerateSiteParamsSchema,
});

const SemanticQuestTurnInActionSchema = z.object({
  action: z.literal('semantic_quest_turn_in'),
  params: SemanticQuestTurnInParamsSchema,
});

export const ConversationExposedActionSchema = z.discriminatedUnion('action', [
  PartyAddMemberActionSchema,
  PartyAddKnownMemberActionSchema,
  SemanticQuestCreateActionSchema,
  SemanticQuestTurnInActionSchema,
]);
export type ConversationExposedAction = z.infer<typeof ConversationExposedActionSchema>;

export const InternalWorkflowActionSchema = SemanticQuestGenerateSiteActionSchema;
export type InternalWorkflowAction = z.infer<typeof InternalWorkflowActionSchema>;

export const ChatInteractionActionSchema = z.discriminatedUnion('action', [
  PartyAddMemberActionSchema,
  PartyAddKnownMemberActionSchema,
  InvokeGenerationTaskActionSchema,
  SemanticQuestCreateActionSchema,
  SemanticQuestGenerateSiteActionSchema,
  SemanticQuestTurnInActionSchema,
]);
export type ChatInteractionAction = z.infer<typeof ChatInteractionActionSchema>;

export const ChatInteractionExecutionResultSchema = z.object({
  ok: z.boolean(),
  action: ChatInteractionActionTypeSchema,
  message: z.string(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type ChatInteractionExecutionResult = z.infer<typeof ChatInteractionExecutionResultSchema>;

export const ConversationPendingIntentSchema = ConversationExposedActionSchema;
export type ConversationPendingIntent = z.infer<typeof ConversationPendingIntentSchema>;

export const ConversationChoiceOptionSchema = z.object({
  id: z.string().min(1),
  sendText: z.string().min(1),
  action: ConversationExposedActionSchema.optional(),
});
export type ConversationChoiceOption = z.infer<typeof ConversationChoiceOptionSchema>;

export const MIN_CONVERSATION_CHOICE_COUNT = 2;
export const MAX_CONVERSATION_CHOICE_COUNT = 4;

export const ConversationChoiceEnvelopeSchema = z.object({
  options: z
    .array(ConversationChoiceOptionSchema)
    .min(MIN_CONVERSATION_CHOICE_COUNT)
    .max(MAX_CONVERSATION_CHOICE_COUNT),
});
export type ConversationChoiceEnvelope = z.infer<typeof ConversationChoiceEnvelopeSchema>;
